import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useDevelopmentDemo } from '../../contexts/DevelopmentDemoContext';
import { illustrativeRubricNote } from '../../data/developmentDomains';
import type { DevSectionId, DevStage } from '../../types/developmentDemo';
import { glassPanel, linkButton } from '../../utils/demoUi';
import { deriveStage, stageLabels } from '../../utils/developmentDemo';
import { StageBadge } from './DevBadges';
const actionFor: Record<DevStage, {
  section: DevSectionId;
  label: string;
}> = {
  'ready-initial': {
    section: 'records',
    label: 'Open record'
  },
  'awaiting-review': {
    section: 'review',
    label: 'Review evidence'
  },
  revision: {
    section: 'records',
    label: 'Open record'
  },
  'awaiting-activity': {
    section: 'plans',
    label: 'Assign activity'
  },
  practising: {
    section: 'plans',
    label: 'View activity'
  },
  'ready-reassessment': {
    section: 'records',
    label: 'Open record'
  },
  'cycle-complete': {
    section: 'progress',
    label: 'Compare rounds'
  }
};
export function DevReviewerOverview() {
  const {
    state,
    config,
    dispatch
  } = useDevelopmentDemo();
  const awaitingReview = state.submissions.filter(submission => !state.reviews.some(review => review.submissionId === submission.id)).length;
  const awaitingActivity = config.participants.filter(participant => deriveStage(state, participant.id) === 'awaiting-activity').length;
  const practising = state.activities.filter(activity => activity.status === 'assigned').length;
  const stats = [{
    label: 'Responses awaiting review',
    value: awaitingReview,
    section: 'review' as DevSectionId
  }, {
    label: 'Reviews awaiting an activity',
    value: awaitingActivity,
    section: 'plans' as DevSectionId
  }, {
    label: 'Activities in progress',
    value: practising,
    section: 'plans' as DevSectionId
  }];
  return <div data-id="element-696" data-mp="22trub7dfasbh">
      <p className="text-sm font-medium text-lavender" data-id="element-697" data-mp="22u9wekmro0rb">{config.organization}</p>
      <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight text-ink-dark lg:text-4xl" data-id="element-698" data-mp="27rt8bdvzs1g3">{config.workspaces.reviewer.title}</h1>
      <p className="mt-2 max-w-2xl text-ink-muted" data-id="element-699" data-mp="1zs5e0excm0lz">{config.description}</p>

      <dl className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3" data-id="element-700" data-mp="1vqti5rnvsj5c">
        {stats.map(stat => <div key={stat.label} className={`${glassPanel} p-5`} data-id="element-701" data-mp="29qum5fzg06p1">
            <dt className="text-sm text-ink-muted" data-id="element-702" data-mp="24qq4vnqpn7xr">{stat.label}</dt>
            <dd className="mt-1 flex items-end justify-between gap-3" data-id="element-703" data-mp="24pf13nx9m06n">
              <span className="font-heading text-3xl font-bold text-ink-dark" data-id="element-704" data-mp="2ipg3j7t6hfo8">{stat.value}</span>
              <button type="button" onClick={() => dispatch({
            type: 'setSection',
            section: stat.section
          })} className={linkButton} data-id="element-705" data-mp="2enuvbzrl9pkd">
                Open
                <ArrowRight size={14} aria-hidden="true" data-id="element-706" data-mp="2nh7sxf0llfcs" />
                <span className="sr-only" data-id="element-707" data-mp="2nhrdl3hmuhui"> {stat.label}</span>
              </button>
            </dd>
          </div>)}
      </dl>

      <section aria-labelledby="participants-title" className="mt-10" data-id="element-708" data-mp="32c4cuphrrr5p">
        <h2 id="participants-title" className="font-heading text-lg font-bold text-ink-dark" data-id="element-709" data-mp="2xh8hpgh8knod">
          {config.participantNoun}s
        </h2>
        <ul className="mt-3 divide-y divide-line border-y border-line" data-id="element-710" data-mp="3l2iscsemrdli">
          {config.participants.map(participant => {
          const stage = deriveStage(state, participant.id);
          const action = actionFor[stage];
          return <li key={participant.id} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-711" data-mp="3c09kybens0cc">
                <div data-id="element-712" data-mp="323wr9dj45mhl">
                  <p className="font-semibold text-ink-dark" data-id="element-713" data-mp="324gbx205eozb">
                    {participant.name} <span className="font-mono text-xs font-normal text-ink-muted" data-id="element-714" data-mp="2ocialdcohxdp">{participant.id}</span>
                  </p>
                  <p className="text-sm text-ink-muted" data-id="element-715" data-mp="2tatjnlv5g3uv">{participant.description} · {stageLabels[stage].reviewer}</p>
                </div>
                <div className="flex shrink-0 items-center gap-4" data-id="element-716" data-mp="2ef42c10wsewh">
                  <StageBadge stage={stage} data-id="element-717" data-mp="2jcdo96p3a8fk" />
                  <button type="button" onClick={() => dispatch({
                type: 'selectParticipant',
                id: participant.id,
                section: action.section
              })} className={linkButton} data-id="element-718" data-mp="2jf9nqzgl5564">
                    {action.label}
                    <ArrowRight size={14} aria-hidden="true" data-id="element-719" data-mp="3cwosw155yu4r" />
                    <span className="sr-only" data-id="element-720" data-mp="3808372ibg7pk"> for {participant.name}</span>
                  </button>
                </div>
              </li>;
        })}
        </ul>
      </section>

      <p className="mt-8 text-sm leading-relaxed text-ink-muted" data-id="element-721" data-mp="2yy8k84nqqtjz">
        {config.note} {illustrativeRubricNote}
      </p>
    </div>;
}